import { makeAutoObservable, toJS } from "mobx"
import type AppStore from "../stores/AppStore"

export const defaultMeasure: IMeasure = {
  id: "",
  uid: "",
  userName: "",
  objective: "",
  department: "",
  perspective: "",
  description: "",
  comments: "",
  activities: "",
  baseline: null,
  rating1: null,
  rating2: null,
  rating3: null,
  rating4: null,
  rating5: null,
  annualTarget: 0,
  midtermActual: null,
  annualActual: null,
  autoRating: 1,
  midtermAutoRating: 1,
  midtermEmployeeRating: null, // Q2 employee rating
  finalEmployeeRating: null, // Q4 employee rating
  midtermSupervisorRating: null, // Q2 supervisor rating (alias for midtermRating)
  finalSupervisorRating: null, // Q4 supervisor rating (alias for finalRating)
  midtermRating: null, // Q2 supervisor rating (original property)
  finalRating: null, // Q4 supervisor rating (original property)
  weight: 0,
  dataType: "Currency",
  dataSymbol: "NAD",
  symbolPos: "prefix",
  sourceOfEvidence: "",
  targetDate: "",
}

export interface IMeasure {
  id: string
  uid: string
  userName: string
  objective: string
  department: string
  perspective: string // Financial, Customer, Operational, Learning & Growth,
  description: string
  comments: string
  activities: string
  baseline: number | null
  rating1: number | null // required field
  rating2: number | null // required field
  rating3: number | null // required field
  rating4: number | null
  rating5: number | null
  annualTarget: number | null
  midtermActual: number | null
  annualActual: number | null
  targetDate: number | string
  autoRating: number
  midtermAutoRating: number

  // Q2 and Q4 Employee Ratings
  midtermEmployeeRating: number | null // Q2 employee self-rating
  finalEmployeeRating: number | null // Q4 employee self-rating

  // Q2 and Q4 Supervisor Ratings (with clear naming)
  midtermSupervisorRating: number | null // Q2 supervisor rating (new clear property)
  finalSupervisorRating: number | null // Q4 supervisor rating (new clear property)

  // Legacy properties (kept for backward compatibility)
  midtermRating: number | null // Q2 supervisor rating (legacy)
  finalRating: number | null // Q4 supervisor rating (legacy)

  weight: number // percentage
  dataType: string // Number | Text | Date | Percentage | Currency | YesNo | Rating
  dataSymbol: string // %, $, €, £
  symbolPos: "prefix" | "suffix" // prefix / suffix
  sourceOfEvidence: string
  isUpdated?: boolean
}

export default class Measure {
  private measure: IMeasure

  constructor(
    private store: AppStore,
    measure: IMeasure,
  ) {
    makeAutoObservable(this)
    this.measure = measure
  }

  get asJson(): IMeasure {
    const measureData = toJS(this.measure)

    // Ensure backward compatibility by syncing new and legacy properties
    if (measureData.midtermSupervisorRating !== null && measureData.midtermRating === null) {
      measureData.midtermRating = measureData.midtermSupervisorRating
    } else if (measureData.midtermRating !== null && measureData.midtermSupervisorRating === null) {
      measureData.midtermSupervisorRating = measureData.midtermRating
    }

    if (measureData.finalSupervisorRating !== null && measureData.finalRating === null) {
      measureData.finalRating = measureData.finalSupervisorRating
    } else if (measureData.finalRating !== null && measureData.finalSupervisorRating === null) {
      measureData.finalSupervisorRating = measureData.finalRating
    }

    return measureData
  }
}
